import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { colors } from "../../../app/theme/colors";
import AppButton from "../../../components/appButton/appButton";
import { useAppDispatch, useAppSelector } from "../../../app/redux/hooks";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useLoginModalStyles } from "./loginModalStyles";
import logo from "../../../images/logoFull.png";
import CustomOutlinedInput from "../../../components/outlinedInput/customOutlinedInput";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ForgotPasswordModal from "../forgotPasswordModal/forgotPasswordModal";
import { LoginRequest } from "../../../models/auth/loginRequest";
import { INIT_LOGIN_REQUEST } from "../../../models/init_data/initData";
import {
  login as loginSlice,
  resetErrorMessage,
  selectCurrentAccount,
  selectErrorMessage,
  selectLoginResponse,
  selectLoginStatus,
} from "../../../slices/authSlice";
import SetPasswordModal from "../setPasswordModal/setPasswordModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { routes } from "../../../app/router/routes";

interface Props {
  open: any;
  handleClose: any;
  closeLoginModal: any;
}

export default function LoginModal({
  handleClose,
  open,
  closeLoginModal,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { classes } = useLoginModalStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const loginResponse = useAppSelector(selectLoginResponse);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const errorMessage = useAppSelector(selectErrorMessage);
  const loginStatus = useAppSelector(selectLoginStatus);

  const [isPasswordVisible, setIsPasswordVisible] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isOpenForgot, setIsOpenForgot] = useState<boolean>(false);
  const [login, setLogin] = useState<LoginRequest>(INIT_LOGIN_REQUEST);
  const [isOpenSetPasswordModal, setIsOpenSetPasswordModal] =
    useState<boolean>(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setLogin((prevState) => ({
      ...prevState,
      email: value,
    }));
    setEmailError(false);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(e.target.value);
    setLogin((prevState) => ({
      ...prevState,
      password: value,
    }));
    setPasswordError(false);
  };

  const handleDashboardPage = (route: string) => {
    navigate(route);
  };

  const handleLogin = () => {
    dispatch(loginSlice(login));
  };

  const handleDialogClose = () => {
    setEmailError(false);
    setPasswordError(false);

    handleClose();
  };

  const resetLoginState = () => {
    setLogin(INIT_LOGIN_REQUEST);
    setPassword("");
    setEmail("");
  };

  useEffect(() => {
    if (loginResponse && loginResponse?.isFirstLogin) {
      setIsOpenSetPasswordModal(true);
    }
  }, [loginResponse]);

  useEffect(() => {
    if (errorMessage === "Failed to login.") {
      toast.error("Uneti podaci nisu ispravni. Proverite i pokusajte ponovo", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: true,
        onClose: () => {
          dispatch(resetErrorMessage());
        },
      });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (loginStatus === "Fulfilled") {
      handleDashboardPage(routes.dashboard);
      dispatch(resetErrorMessage());
      handleClose();
      resetLoginState();
    }
  }, [loginStatus]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="xs"
      id="fullDetailsModal_dialog"
      PaperProps={{ sx: { borderRadius: "15px", overflow: "hidden" } }}
    >
      <IconButton
        edge="end"
        color="inherit"
        onClick={handleDialogClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: theme.spacing(1),
          right: theme.spacing(2),
        }}
      >
        <CloseIcon className={classes.closeIcon} />
      </IconButton>
      <DialogContent className={classes.dialogContent}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          padding={"5px"}
          sx={{ textAlign: "center" }}
        >
          <Grid item md={12} xs={12}>
            <img src={logo} alt="Logo" className={classes.logoImage} />
          </Grid>
          <Grid item md={12} xs={12} mt={5}>
            <CustomOutlinedInput
              id="loginModal_input_email"
              type="email"
              label={t("loginModal.loginEmail")}
              width="100%"
              labelColor={colors.primary}
              onChange={handleEmailChange}
              value={email}
              error={emailError}
              errorMessage={t("loginModal.loginErrorEmail")}
            />
          </Grid>
          <Grid item md={12} xs={12} mt={3}>
            <CustomOutlinedInput
              id="loginModal_input_password"
              label={t("loginModal.loginPassword")}
              type={isPasswordVisible ? "password" : "text"}
              width="100%"
              labelColor={colors.primary}
              onChange={handlePasswordChange}
              value={password}
              error={passwordError}
              endAdornment={
                isPasswordVisible ? (
                  <VisibilityOffIcon
                    onClick={() => setIsPasswordVisible(false)}
                    className={classes.visibilityIcon}
                  />
                ) : (
                  <VisibilityIcon
                    onClick={() => setIsPasswordVisible(true)}
                    className={classes.visibilityIcon}
                  />
                )
              }
              errorMessage={t("loginModal.loginErrorPassword")}
            />

            <Typography
              id="loginModal_label_forgotPassword"
              variant="body1"
              className={classes.forgotPasswordText}
              onClick={() => setIsOpenForgot(true)}
            >
              {t("loginModal.loginForgotPassword")}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <AppButton
          id="loginModal_button_close"
          onClick={handleLogin}
          color={colors.primary}
          hover={colors.primary}
          label={t("buttons.login")}
          width="100%"
        />
      </DialogActions>
      <ForgotPasswordModal
        handleClose={() => setIsOpenForgot(false)}
        open={isOpenForgot}
      />
      <SetPasswordModal
        handleClose={() => setIsOpenSetPasswordModal(false)}
        open={isOpenSetPasswordModal}
        currentAccount={currentAccount}
        login={login}
        setIsOpenSetPasswordModal={setIsOpenSetPasswordModal}
        closeLoginModal={closeLoginModal}
      />
    </Dialog>
  );
}
